import { VStack } from '@genial/components';
import React from 'react';
import { View } from 'react-native';
import { useRecoilValue } from 'recoil';

import {
  advisorIdToApisSelector,
  partnerIdToApisSelector,
} from '../../../../../store/master/selector';
import { PROCEED_RULE_MESSAGE } from '../../constants';
import { FixedFeeNewContractStepOneData } from '../../types';

import { ContractRateItem } from './components/ContractRateItem';
import { CustomerSearchItem } from './components/CustomerSearchItem';
import { CustomerSearchResults } from './components/CustomerSearchResults';
import { ExclusionItem } from './components/ExclusionItem';
import { NetWorthItem } from './components/NetWorthItem';
import { OverwriteTermNotice } from './components/OverwriteTermNotice';
import { useStepOneAvailableConfigs } from './hooks/useStepOneAvailableConfigs';
import { useStepOneCustomerResultsMenu } from './hooks/useStepOneCustomerResultsMenu';
import { useStepOneCustomerSearch } from './hooks/useStepOneCustomerSearch';
import { useStepOneCustomerSelection } from './hooks/useStepOneCustomerSelection';
import { useStepOneNetWorth } from './hooks/useStepOneNetWorth';
import { useStepOneProceedRules } from './hooks/useStepOneProceedRules';
import { useStepOneRate } from './hooks/useStepOneRate';
import { useStepOneTermReplacementRestriction } from './hooks/useStepOneTermReplacementRestriction';

interface FixedFeeNewContractStepOneProps {
  value: FixedFeeNewContractStepOneData;
  onChange: (value: FixedFeeNewContractStepOneData) => void;
  initialReplacementSelection?: {
    pnpAccountNumber: number;
  };
}

export const FixedFeeNewContractStepOne: React.FC<FixedFeeNewContractStepOneProps> = ({
  value,
  onChange,
  initialReplacementSelection,
}) => {
  const advisorSelected = useRecoilValue(advisorIdToApisSelector);
  const partnerSelected = useRecoilValue(partnerIdToApisSelector);
  const updateState = React.useCallback(
    (nextValue: Partial<FixedFeeNewContractStepOneData>) => {
      onChange({
        ...value,
        ...nextValue,
      });
    },
    [onChange, value]
  );

  const { adjustedNetWorth, loadingNetWorth, netWorthError, netWorthRefresher } =
    useStepOneNetWorth({
      accountNumber: value.selectedCustomer?.accountNumber,
      excludeEquity: value.excludeEquity,
    });

  const { availableConfigs, loadingAvailableRateRanges } = useStepOneAvailableConfigs({
    advisorId: advisorSelected,
    partnerId: partnerSelected || 0,
  });

  const {
    isTermReplacementRestricted,
    isTermReplacementStatusLoading,
    handleTermReplacementRestrictionChange,
  } = useStepOneTermReplacementRestriction();

  const {
    loadingCustomersSearch,
    customerSearchResultItems,
    showNotFoundMessage,
    canShowSearchResults,
  } = useStepOneCustomerSearch({
    searchTerm: value.searchTerm,
    hasSelectedCustomer: !!value.selectedCustomer,
  });

  const {
    isCustomerResultsMenuOpen,
    customerSearchInputWidth,
    closeMenu,
    handleCustomerSearchContainerLayout,
  } = useStepOneCustomerResultsMenu({
    hasSelectedCustomer: !!value.selectedCustomer,
    customerSearchResultItemsLength: customerSearchResultItems.length,
    showNotFoundMessage,
    canShowSearchResults,
  });

  const { handleSearchTermChange, handleSelectCustomer, handleClearSearch } =
    useStepOneCustomerSelection({
      value,
      initialReplacementSelection,
      customerSearchResultItems,
      updateState,
      closeMenu,
    });

  const { handleRateChange } = useStepOneRate({ updateState });

  const { hasNetWorthProblem } = useStepOneProceedRules({
    value,
    adjustedNetWorth,
    loadingNetWorth,
    loadingAvailableRateRanges,
    isTermReplacementRestricted,
    isTermReplacementStatusLoading,
    onChange,
  });

  return (
    <VStack space="stack.xxxs">
      <View onLayout={handleCustomerSearchContainerLayout}>
        <CustomerSearchItem
          searchTerm={value.searchTerm}
          selectedCustomer={value.selectedCustomer}
          loading={loadingCustomersSearch}
          customError={hasNetWorthProblem ? PROCEED_RULE_MESSAGE : undefined}
          onSearchTermChange={handleSearchTermChange}
          onClearSearch={handleClearSearch}
        />

        <CustomerSearchResults
          items={customerSearchResultItems}
          selectedCustomer={value.selectedCustomer}
          isOpen={isCustomerResultsMenuOpen}
          onClose={closeMenu}
          menuWidth={customerSearchInputWidth}
          showNotFoundMessage={showNotFoundMessage}
          onSelectCustomer={handleSelectCustomer}
          onClearSearch={handleClearSearch}
        />
      </View>

      <OverwriteTermNotice
        selectedCustomer={value.selectedCustomer}
        adjustedNetWorth={adjustedNetWorth}
        rate={value.rate}
        isNetWorthLoading={loadingNetWorth}
        onTermReplacementRestrictionChange={handleTermReplacementRestrictionChange}
      />

      <NetWorthItem
        isLoading={loadingNetWorth}
        netWorthError={netWorthError}
        netWorthRefresher={netWorthRefresher}
        hasSelectedCustomer={!!value.selectedCustomer}
        adjustedNetWorth={adjustedNetWorth}
      />

      <ContractRateItem
        availableConfigs={availableConfigs}
        loadingAvailableRateRanges={loadingAvailableRateRanges}
        rate={value.rate}
        onRateChange={handleRateChange}
      />

      <ExclusionItem
        availableConfigs={availableConfigs}
        loadingAvailableRateRanges={loadingAvailableRateRanges}
        excludeEquity={value.excludeEquity}
        onChange={(excludeEquity) => updateState({ excludeEquity })}
      />
    </VStack>
  );
};
